import {
  useState,
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  setItem,
  getItem,
  setDataToIndexDB,
  getDataFromIndexDB,
  deleteDataFromIndexDB,
} from 'lib/Storage';

export const UploadContext = createContext(null);

export function UploadProvider({ children }) {
  const [drafts, setDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [totalTime, setTotalTime] = useState(0);
  const [tempo, setTempo] = useState(-1);
  const [layers, setLayers] = useState(Array(5).fill(null));
  const [play, setPlay] = useState(false);
  const [volume, setVolume] = useState(100);
  const [time, setTime] = useState(0);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [leftBoundary, setLeftBoundary] = useState(0);
  const [rightBoundary, setRightBoundary] = useState(0);
  const [manualCurrentTimeMusic, setManualCurrentTimeMusic] = useState(null);
  const [bares, setBares] = useState([]);
  const [bareBandiType, setBareBandiType] = useState(null);
  const [layersUpdated, setLayersUpdated] = useState(false);
  const [mutes, setMutes] = useState(Array(5).fill(false));
  const [speed, setSpeed] = useState(1);
  const audioContextRef = useRef(null);
  const isInitialRender = useRef(true);
  const [rightTab, setRightTab] = useState(null);
  const [leftTab, setLeftTab] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleResetProject = () => {
    setTotalTime(0);
    setTempo(-1);
    setLayers(Array(5).fill(null));
    setTime(0);
    setSelectedLayer(null);
    setLeftBoundary(0);
    setRightBoundary(0);
    setBares([]);
    setBareBandiType(null);
    setLayersUpdated(false);
    setMutes(Array(5).fill(false));
    setSpeed(1);
    setRightTab(null);
    setSelectedDraft(null);
    localStorage.removeItem('uploadState');
    localStorage.removeItem('draft');
    deleteDataFromIndexDB('layers');
  };

  const fetchStatesFromStorage = async() => {
    const savedState = getItem('uploadState');
    const storedDraft = getItem('draft');
    if (savedState) {
      const {
        tempo,
        volume,
        bares,
        mutes,
      } = JSON.parse(savedState);
      setTempo(tempo ?? -1);
      setVolume(volume ?? 100);
      setBares(bares ?? []);
      setMutes(mutes ?? Array(5).fill(false));
      setSelectedDraft(JSON.parse(storedDraft));
    }
  };

  const fetchLayersFromIndexDB = async() => {
    let maxTime = 0;
    let saveddLayers = await getDataFromIndexDB('layers');
    if(!Array.isArray(saveddLayers) || !saveddLayers.length) {
      saveddLayers = Array(5).fill(null);
      setLeftTab(null);
    } else {
      const mapedLayers = saveddLayers.map(({value})=> value);
      saveddLayers = Array(5).fill(null).map((_, i) =>{
        const layer = mapedLayers.find((item)=> (item && item.id === i));
        return layer ? layer : null
      });
      setLeftTab('layers');
    }

    for (let i = 0; i < saveddLayers.length; i++) {
      if (saveddLayers[i] && saveddLayers[i].length > maxTime) {
        maxTime = saveddLayers[i].length;
      }
    }
    setTotalTime(maxTime);
    setLayers(saveddLayers);
    setLayersUpdated(true);
    return saveddLayers
  };

  const handleStoreStates = () => {
    const stateToSave = {
      tempo,
      volume,
      bares,
      mutes,
    };
    setItem('uploadState', JSON.stringify(stateToSave));
  };

  const handleStoreLayers = async () => {
    setDataToIndexDB('layers', layers);
  };

  const layersNotEmpty = (layersList) => (layersList.some((layer) => layer?.file));

  const initState = async () => {
    setLoading(true);
    await fetchStatesFromStorage();
    const fetchedLayers = await fetchLayersFromIndexDB();
    setLoading(layersNotEmpty(fetchedLayers));
  };

  useEffect(() => {
    initState();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    handleStoreStates();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [ tempo, volume, bares, mutes, selectedDraft]);

  useEffect(() => {
    setRightBoundary(totalTime);
  }, [totalTime]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      handleStoreLayers();
    }
   
    if (tempo <= 0) {
      setBareBandiType(null);
    }
  }, [layers]);

  useEffect(() => {
    setItem('draft', JSON.stringify(selectedDraft));
  }, [selectedDraft]);

  useEffect(() => {
    if(bares.length === 2){
      setBares([])
    }
  }, [bares]);


  const memoedValue = useMemo(() => {
    const layersIsActive = layersNotEmpty(layers);
    const inProcess = ['layers', 'layerInfo', 'layerSaveConfirm', 'layerDeleteConfirm'].includes(leftTab);

    return {
      play,
      setPlay,
      volume,
      setVolume,
      time,
      setTime,
      tempo,
      setTempo,
      handleResetProject,
      totalTime,
      setTotalTime,
      layers,
      setLayers,
      selectedLayer,
      setSelectedLayer,
      leftBoundary,
      setLeftBoundary,
      rightBoundary,
      setRightBoundary,
      bares,
      setBares,
      bareBandiType,
      setBareBandiType,
      layersUpdated,
      setLayersUpdated,
      mutes,
      setMutes,
      speed,
      setSpeed,
      drafts,
      setDrafts,
      selectedDraft,
      setSelectedDraft,
      inProcess,
      layersIsActive,
      audioContextRef,
      rightTab,
      setRightTab,
      leftTab,
      setLeftTab,
      loading,
      setLoading,
      manualCurrentTimeMusic,
      setManualCurrentTimeMusic,
    };
  }, [
    play,
    setPlay,
    volume,
    setVolume,
    time,
    setTime,
    tempo,
    setTempo,
    totalTime,
    setTotalTime,
    layers,
    setLayers,
    selectedLayer,
    setSelectedLayer,
    leftBoundary,
    setLeftBoundary,
    rightBoundary,
    setRightBoundary,
    bares,
    setBares,
    bareBandiType,
    setBareBandiType,
    layersUpdated,
    setLayersUpdated,
    mutes,
    setMutes,
    speed,
    setSpeed,
    drafts,
    setDrafts,
    selectedDraft,
    setSelectedDraft,
    audioContextRef,
    rightTab,
    setRightTab,
    leftTab,
    setLeftTab,
    loading,
    setLoading,
    manualCurrentTimeMusic,
    setManualCurrentTimeMusic,
  ]);

  return (
    <UploadContext.Provider value={memoedValue}>
      {children}
    </UploadContext.Provider>
  );
}

export default function useUploadContext() {
  return useContext(UploadContext);
}
